import { useState, useCallback, useRef } from 'react';
import { v2ChatApi } from '@/services/v2-chat-api';
import type { UploadResult } from '@/types/chat';

const ACCEPTED_TYPES = new Set([
  'image/jpeg', 'image/png', 'image/webp', 'application/pdf',
  'text/csv', 'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
const MAX_FILES = 5;

export interface PendingFile {
  file: File;
  previewUrl: string;
  id: string;
}

export interface UseFileUploadReturn {
  pendingFiles: PendingFile[];
  isUploading: boolean;
  addFiles: (files: FileList | File[]) => string | null;
  removeFile: (id: string) => void;
  uploadAll: () => Promise<string[]>;
  clear: () => void;
}

let fileCounter = 0;

export function useFileUpload(): UseFileUploadReturn {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const previewUrlsRef = useRef<string[]>([]);

  const addFiles = useCallback((files: FileList | File[]): string | null => {
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      if (!ACCEPTED_TYPES.has(file.type)) {
        return `Unsupported file type: ${file.type}. Accepted: JPEG, PNG, WebP, PDF, CSV, TXT, DOCX, XLSX`;
      }
      if (file.size > MAX_FILE_SIZE) {
        return `File too large: ${(file.size / 1024 / 1024).toFixed(1)} MB (max 25 MB)`;
      }
    }

    setPendingFiles(prev => {
      const remaining = MAX_FILES - prev.length;
      if (remaining <= 0) return prev;

      const toAdd = fileArray.slice(0, remaining).map(file => {
        const isImage = file.type.startsWith('image/');
        const previewUrl = isImage ? URL.createObjectURL(file) : '';
        if (isImage) previewUrlsRef.current.push(previewUrl);
        return {
          file,
          previewUrl,
          id: `file-${++fileCounter}`,
        };
      });

      return [...prev, ...toAdd];
    });

    if (fileArray.length > MAX_FILES) {
      return `Maximum ${MAX_FILES} files per message`;
    }

    return null;
  }, []);

  const removeFile = useCallback((id: string) => {
    setPendingFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file) {
        URL.revokeObjectURL(file.previewUrl);
      }
      return prev.filter(f => f.id !== id);
    });
  }, []);

  const uploadAll = useCallback(async (): Promise<string[]> => {
    if (pendingFiles.length === 0) return [];

    setIsUploading(true);
    try {
      const results: UploadResult[] = await Promise.all(
        pendingFiles.map(pf => v2ChatApi.uploadFile(pf.file)),
      );
      return results.map(r => r.upload_id);
    } finally {
      setIsUploading(false);
    }
  }, [pendingFiles]);

  const clear = useCallback(() => {
    for (const url of previewUrlsRef.current) {
      URL.revokeObjectURL(url);
    }
    previewUrlsRef.current = [];
    setPendingFiles([]);
  }, []);

  return {
    pendingFiles,
    isUploading,
    addFiles,
    removeFile,
    uploadAll,
    clear,
  };
}
